import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IFindRequestParams, IRequestCallback } from '@df/api/request/request.service';
import { IModelData, IModelDataV3WithUrl, Model, TLoadParams } from '@df/catalog/model';
import { Resource } from '@df/catalog/resource';
import { NICmsApi } from './../../data/icms-api.interfaces';
import { ICmsModel } from './icms-model';

@Injectable()
export abstract class ICmsModelResource<T extends ICmsModel<IModelData, I>, I extends number | string = number | string> extends Resource<
    T,
    I
> {
    readonly isCmsContent = true;

    private router = inject(Router);

    private location = inject(Location);

    override find(findOptions: IFindRequestParams, params?: Partial<TLoadParams>): Promise<T[]> {
        if (this.app.client?.isCms && findOptions.verbosity === 3) {
            findOptions.verbosity = 4;
        }
        return super.find(findOptions, params);
    }

    /**
     * Saves iCms model
     */
    async save(model: T): Promise<NICmsApi.ISaveResponse> {
        const data: NICmsApi.ISaveRequest<IModelData, I> = {
            id: Number(model.id) < 0 ? undefined : model.id,
            type: model.type,
            data: model.rawData!
        };

        model.allowDataOverwrite = true;
        let response: NICmsApi.ISaveResponse<I> | null;
        try {
            response = await this.requestService.send<NICmsApi.ISaveResponse<I>>(undefined, {
                path: 'cms/save',
                data,
                api: 'node',
                token: true
            });
            model.allowDataOverwrite = true;
        } catch (err) {
            model.allowDataOverwrite = false;
            throw err;
        }

        this.onModelSave(model, response!.result.id);

        return response!;
    }

    /**
     * Saves iCms model
     */
    async saveChange(model: T, change: NICmsApi.IDataChange | NICmsApi.IObjectDataChange): Promise<NICmsApi.ISaveResponse> {
        const data: NICmsApi.ISaveChangeRequest<IModelData, I> = {
            id: model.id,
            type: model.type,
            change
        };

        const response = await this.requestService.send<NICmsApi.ISaveResponse<I>>(undefined, {
            path: 'cms/saveChange',
            data,
            api: 'node',
            token: true
        });

        this.onModelSave(model, response!.result.id);

        return response!;
    }

    /**
     * Delete iCms model
     */
    async delete(model: T): Promise<NICmsApi.IDeleteResponse> {
        const data: NICmsApi.IDeleteRequest = {
            type: model.type,
            id: model.id
        };

        if (Number(model.id) < 0) {
            model.unload();
            return { removed: [{ id: model.id, type: model.type }] };
        }

        const resource = this.app.resources[model.type];

        const response = await this.app.request.send<NICmsApi.IDeleteResponse>(undefined, {
            path: 'cms/delete',
            data,
            api: 'node',
            token: true
        });

        response!.removed.forEach(item => resource.unload(item.id));

        return response!;
    }

    /**
     * TODO move this to global observer used only in icms listing to data changes
     */
    private onModelSave(model: T, savedModelId: I) {
        const newModel = this.getExisting(savedModelId);
        // redirect to new location if it was active model
        if (model.routable && (this.app.meta.model as any as ICmsModel) === model) {
            const newUrl = (newModel?.data as IModelDataV3WithUrl).url;
            if (newUrl && this.router.url !== newUrl) {
                this.location.replaceState(newUrl);
                this.app.meta.model = model as any as Model<IModelDataV3WithUrl, I>;
            }
        }

        // replace existing model with the new one if it was a new thing
        if (newModel && Number(model.id) < 0) {
            model.merge(newModel);
        }
    }
}
